import React, { useState, useEffect } from 'react';
import {
  Plus, Loader2, Target, Calendar, ChevronRight, BarChart3,
  Send, Clock, Pause, CheckCircle2, LayoutGrid
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-50 text-stone-500', icon: Clock },
  generating: { label: 'Genererer...', color: 'bg-amber-50 text-amber-600', icon: Loader2 },
  review: { label: 'Under review', color: 'bg-blue-50 text-blue-600', icon: BarChart3 },
  scheduled: { label: 'Planlagt', color: 'bg-indigo-50 text-indigo-600', icon: Calendar },
  active: { label: 'Active', color: 'bg-green-50 text-green-600', icon: Send },
  paused: { label: 'Pauset', color: 'bg-amber-50 text-amber-600', icon: Pause },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle2 },
};

const GOAL_LABELS = {
  drive_traffic: 'Drive trafikk',
  promote_product: 'Promoter product',
  build_awareness: 'Bygg visiblehet',
  get_saves: 'Get saves',
  educational_growth: 'Edukativt innhold',
};

const CampaignList = ({ onCreateNew, onOpenCampaign, onReviewCampaign }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/campaigns`, getAuth());
        setCampaigns(res.data.campaigns || []);
      } catch (e) {
        console.error('Fetch campaigns error:', e);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Kampanjer</h2>
          <p className="font-sans text-xs text-stone-400 mt-0.5">
            {campaigns.length === 0 ? 'No campaigns yet' : `${campaigns.length} kampanje${campaigns.length > 1 ? 'r' : ''}`}
          </p>
        </div>
        <Button
          data-testid="new-campaign-btn"
          onClick={onCreateNew}
          className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-5 py-2.5 text-xs font-sans font-semibold"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Ny kampanje
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#E60023]/10 flex items-center justify-center mx-auto mb-4">
            <LayoutGrid className="w-7 h-7 text-[#E60023]" />
          </div>
          <h3 className="font-serif font-bold text-stone-800 text-lg mb-1">Create your first campaign</h3>
          <p className="font-sans text-xs text-stone-400 max-w-sm mx-auto leading-relaxed mb-4">
            Select products, let Lumera generate a complete Pinterest strategy with smart variation, and publish everything with one click.
          </p>
          <Button
            onClick={onCreateNew}
            className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-6 py-3 text-xs font-sans font-semibold"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ny kampanje
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const statusInfo = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusInfo.icon;
            const stats = c.stats || {};
            const total = c.total_pins || 0;
            const published = stats.published || 0;
            const pct = total > 0 ? Math.round((published / total) * 100) : 0;

            return (
              <button
                key={c.id}
                onClick={() => c.status === 'review' ? onReviewCampaign(c.id) : onOpenCampaign(c.id)}
                className="w-full bg-white rounded-2xl border border-stone-100 p-5 text-left hover:shadow-md hover:border-stone-200 transition-all group"
                data-testid={`campaign-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1">
                      <h3 className="font-serif font-bold text-stone-800 text-sm truncate">{c.name}</h3>
                      <span className={`inline-flex items-center gap-1 text-[9px] font-sans font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusInfo.color}`}>
                        <StatusIcon className="w-2.5 h-2.5" /> {statusInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-sans text-stone-400">
                      <span>{total} pins</span>
                      {c.goal && <span className="text-[#B49A88]">{GOAL_LABELS[c.goal] || c.goal}</span>}
                      {c.products?.length > 0 && (
                        <span>{c.products.length} product{c.products.length > 1 ? 'er' : ''}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 transition-colors flex-shrink-0 mt-1" />
                </div>

                {/* Mini progress */}
                {total > 0 && (
                  <div className="mt-3">
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E60023] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-3">
                        {stats.needs_review > 0 && <span className="text-[9px] font-sans text-amber-500">{stats.needs_review} trenger review</span>}
                        {stats.scheduled > 0 && <span className="text-[9px] font-sans text-indigo-500">{stats.scheduled} planned</span>}
                        {stats.published > 0 && <span className="text-[9px] font-sans text-green-500">{stats.published} publisert</span>}
                      </div>
                      <span className="text-[9px] font-sans text-stone-300">{pct}%</span>
                    </div>
                  </div>
                )}

                {/* Date range */}
                {(c.start_date || c.end_date) && (
                  <div className="flex items-center gap-1.5 mt-2 text-[9px] font-sans text-stone-300">
                    <Calendar className="w-2.5 h-2.5" />
                    {c.start_date ? new Date(c.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—'}
                    {' → '}
                    {c.end_date ? new Date(c.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—'}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignList;
